<template>
  <div class="gw-tab">
    <watch-scope-banner />

    <sources-results-card
      :results="results"
      :selection="selection"
      @open-video="video.openFor"
    />

    <generation-action-bar
      :count="selectedCount"
      mode="sources"
      :site-id="selectedSiteId"
      :show-rappel-conso="showRappelConso"
      :busy="isSending"
      :progress="sendProgress"
      :primary-disabled="primaryDisabled"
      :primary-label="primaryLabel"
      @clear="clearSelection"
      @primary="onPrimary"
      @rappel-conso="submit.submitAggregated"
    />

    <video-angles-dialog :video="video" />

    <progress-bar
      title="🤖 Votre agent IA est sur le coup..."
      :show-download-state="false"
      :download="submit.download"
      :steps="steps"
    />

    <v-snackbar
      v-model="video.snackbar.show"
      :color="video.snackbar.color"
      :timeout="video.snackbar.timeout"
      top
      class="snackbar-center"
    >
      <strong>{{ video.snackbar.message }}</strong>
      <template v-slot:action>
        <rmr-action-button
          v-for="action in video.snackbar.actions"
          :key="action.text"
          :label="action.text"
          variant="translucent"
          size="sm"
          @click="action.onClick()"
        />
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="submit.snackbar.show"
      :color="submit.snackbar.color"
      :timeout="submit.snackbar.timeout"
      top
      class="snackbar-center"
    >
      <div>
        <strong>{{ submit.snackbar.message }}</strong>
        <ul v-if="submit.snackbar.details && submit.snackbar.details.length">
          <li
            v-for="(item, index) in submit.snackbar.details"
            :key="`${item.topic}-${index}`"
          >
            {{ item.topic }} :
            {{ item.status === "success" ? "✅ OK" : "❌ Erreur" }}
          </li>
        </ul>
      </div>

      <template v-slot:action>
        <rmr-action-button
          v-for="action in submit.snackbar.actions"
          :key="action.text"
          :label="action.text"
          variant="translucent"
          size="sm"
          @click="action.onClick()"
        />
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";

import ProgressBar from "@/components/common/ProgressBar";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";

import GenerationActionBar from "./GenerationActionBar.vue";
import SourcesResultsCard from "./SourcesResultsCard.vue";
import WatchScopeBanner from "./WatchScopeBanner.vue";
import VideoAnglesDialog from "./parts/VideoAnglesDialog.vue";
import { useGlobalWatchResults } from "./useGlobalWatchResults";
import { useGlobalWatchSelection } from "./useGlobalWatchSelection";
import { GENERATION_STEPS, useGlobalWatchSubmit } from "./useGlobalWatchSubmit";
import { useGlobalWatchVideo } from "./useGlobalWatchVideo";

export default {
  name: "GlobalWatchTab",
  components: {
    GenerationActionBar,
    ProgressBar,
    RmrActionButton,
    SourcesResultsCard,
    VideoAnglesDialog,
    WatchScopeBanner,
  },
  setup() {
    const selection = useGlobalWatchSelection();

    // A new search invalidates the previous selection.
    const results = useGlobalWatchResults({
      onNewSearch: () => {
        selection.clear();
      },
    });

    const submit = useGlobalWatchSubmit({
      selectedItems: selection.selectedItems,
      onSubmitted: (submissionResults = []) => {
        const hasSuccess = submissionResults.some((r) => r.status === "success");
        if (hasSuccess) {
          selection.clear();
          results.reload();
        }
      },
    });

    // Dépliage unitaire d'une vidéo : conservé pour la découpe en time codes
    const video = useGlobalWatchVideo({
      onSubmitted: () => {
        selection.clear();
        results.reload();
      },
    });

    // =>  COMPUTED
    const isVideo = computed(() => results.isVideo.value);

    const primaryLabel = computed(() => {
      if (isVideo.value) {
        return selection.count.value > 1
          ? "Ouvrir une seule vidéo à la fois"
          : "Proposer des angles";
      }
      return selection.count.value > 1
        ? "Générer les brouillons"
        : "Générer le brouillon";
    });

    const showRappelConso = computed(
      () =>
        selection.count.value > 1 &&
        submit.isRappelConsoSelection.value
    );

    const primaryDisabled = computed(() => {
      if (isVideo.value) {
        return selection.count.value !== 1;
      }
      return selection.count.value === 0;
    });

    // =>  METHODS
    const onPrimary = async () => {
      // En vidéo, on conserve le dialogue d'angles unitaire
      if (isVideo.value) {
        await video.openFor(selection.selectedItems.value[0]);
        return;
      }

      await submit.submitArticles();
    };

    // =>  DATA
    return {
      clearSelection: selection.clear,
      isSending: submit.isSending,
      onPrimary,
      primaryDisabled,
      primaryLabel,
      results,
      selectedCount: selection.count,
      selectedSiteId: submit.selectedSiteId,
      selection,
      sendProgress: submit.sendProgress,
      showRappelConso,
      steps: GENERATION_STEPS,
      submit,
      video,
    };
  },
};
</script>

<style lang="scss" scoped>
.gw-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 96px;
}
</style>
